import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import AutomaticSheet from 'src/components/sheet/AutomaticSheet'

import { useGetPlanPlanTransportModesFromLonLatToLonLatGet } from 'src/generated/brezavta/queries'

import Chip from '@mui/material/Chip'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'

import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, CircularProgress } from '@mui/material'
import { useNavigate } from '@tanstack/react-router'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import PlanDisplay from 'src/components/plan/PlanDisplay'
import SheetHeader from 'src/components/sheet/SheetHeader'
import { Leg, Plan, PlanWrapper } from 'src/generated/brezavta/requests'
import { formatDuration } from 'src/lib/util'

export const Route = createFileRoute('/plan/$mode/$coordinates/$datetime/$type/$page/')({
    component: () => <PlanRoute />,
})

function PlanRoute() {
    const navigate = useNavigate()
    const { mode, coordinates, datetime, type, page } = Route.useParams()
    const { t } = useTranslation()

    const { data: plans } = useGetPlanPlanTransportModesFromLonLatToLonLatGet({
        path: {
            from_lon_lat: coordinates!.split(';')[0],
            to_lon_lat: coordinates!.split(';')[1],
            transport_modes: mode,
        },
        query: {
            date: datetime!.split('T')[0],
            time: datetime!.split('T')[1].substring(0, 5),
            page_cursor: page == 'null' ? '' : page,
            type: type,
        },
    })

    const gotoPage = (page: string) => {
        navigate({
            to: '/plan/' + mode + '/' + coordinates + '/' + datetime + '/' + type + '/' + page,
        })
    }

    const gotoPlanView = (plan: Plan, index: number) => {
        navigate({
            to: '/plan/' + mode + '/' + coordinates + '/' + datetime + '/' + type + '/' + page + '/' + index,
            state: {
                plan: plan,
            },
        })
    }

    // Show spinner by default
    let content = (
        <Box sx={{ display: 'flex', height: window.innerHeight / 2, justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
        </Box>
    )

    // Show plans if available
    if (plans) {
        content = (
            <List>
                { // Previous page button
                    plans.previous_page && (
                        <NextPrevPageButton IconClass={ExpandLessIcon} text={t('Show earlier plans')} onClick={() => gotoPage(plans.previous_page)} />
                    )
                }
                {/* The actual plan options */}
                <Plans plans={plans} onClick={gotoPlanView} />
                { // Next page button
                    plans.next_page && (
                        <NextPrevPageButton IconClass={ExpandMoreIcon} text={t('Show later plans')} onClick={() => gotoPage(plans.next_page)} />
                    )
                }
            </List>
        )
    }

    return (
        <AutomaticSheet>
            <SheetHeader heading="Iskanje poti">
                {type == 'DEPART' ? t('Departure') : t('Arrival')}
                {': '}
                {DateTime.fromISO(datetime).toFormat('d. M. yyyy H:mm')}
            </SheetHeader>
            {content}
        </AutomaticSheet>
    )
}

function NextPrevPageButton({ IconClass, text, onClick }: { IconClass: React.FC, text: string, onClick: () => void }) {
    return (
        <ListItemButton
            key="next"
            sx={{ width: '100%', justifyContent: 'left', alignItems: 'left', display: 'flex', flexDirection: 'column', color: 'primary.main' }}
            onClick={onClick}
        >
            <ListItemText
                primary={(
                    <>
                        <IconClass />
                        {text}
                    </>
                )}
            />
        </ListItemButton>
    )
}

function Plans({ plans, onClick }: { plans: PlanWrapper, onClick: (plan: Plan, index: number) => void }) {
    const { t } = useTranslation()
    if (plans?.plans.length == 0) {
        return (
            <>
                {t('No plans found')}
            </>
        )
    }

    return plans?.plans.map((plan, index) => (
        <ListItemButton
            key={index}
            sx={{
                width: '100%',
                borderBottom: '1px solid #f0f0f0',
                flexDirection: 'column',
                alignItems: 'space-between',
            }}
            onClick={() => onClick(plan, index)}
        >
            <ListItemText
                primary={(
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                        <span>
                            {DateTime.fromMillis(plan.start_time, { zone: 'Europe/Ljubljana' }).toFormat('HH:mm') + ' - ' + DateTime.fromMillis(plan.end_time, { zone: 'Europe/Ljubljana' }).toFormat('HH:mm')}
                        </span>
                        <span style={{ float: 'right' }}>
                            <Chip
                                label={formatDuration(plan.end_time - plan.start_time) + ' ' + numTranfersText(t, plan.legs)}
                                variant="outlined"
                                size="small"
                            />
                        </span>
                    </div>
                )}
                sx={{ width: '100%' }}
            />
            <ListItemText
                sx={{ width: '100%' }}
                primary={
                    <PlanDisplay plan={plan} />
                }
            />
        </ListItemButton>
    ))
}

function numTranfersText(t: TFunction, legs: Leg[]) {
    const countTransfers = legs.filter(leg => leg.mode !== 'WALK').length - 1
    if (countTransfers < 1)
        return ''
    return t('{{count}} transfers', { count: countTransfers })
}

export default PlanRoute
